import { gql, useApolloClient } from '@apollo/client';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { FlatDiffItemFragment } from './diffHelpers';

const UseMultipleDiffsQuery = gql<
  GraphQLTypes.UseMultipleDiffsQuery | undefined,
  GraphQLTypes.UseMultipleDiffsQueryVariables
>`
  query UseMultipleDiffsQuery(
    $graphId: ID!
    $newHash: SHA256
    $oldHash: SHA256
  ) {
    service(id: $graphId) {
      id
      flatDiff(newSdlHash: $newHash, oldSdlHash: $oldHash) {
        ... on FlatDiff {
          id
          diff {
            ... on FlatDiffItemCoordinate {
              coordinate
            }
            ... on FlatDiffItemValue {
              value
            }
            ...FlatDiffItemFragment
          }
        }
      }
    }
  }
  ${FlatDiffItemFragment}
`;
export function useMultipleDiffs(
  graphId: string | undefined,
  subgraphHashes: Record<
    string,
    | { oldHash?: string; newHash: string }
    | { oldHash: string; newHash?: string }
  >,
) {
  const apolloClient = useApolloClient();
  const watchQueries = useMemo(
    () =>
      _.mapValues(subgraphHashes, (variables) => {
        return graphId
          ? apolloClient.watchQuery({
              query: UseMultipleDiffsQuery,
              variables: { graphId, ...variables },
            })
          : null;
      }),
    [apolloClient, graphId, subgraphHashes],
  );
  const [responses, setResponses] = useState(() =>
    _.mapValues(watchQueries, (query) => query?.getCurrentResult()),
  );

  useEffect(() => {
    const subscriptions = Object.entries(watchQueries).map(
      ([subgraph, query]) => {
        const updateResponse = () => {
          setResponses((current) => ({
            ...current,
            [subgraph]: query?.getCurrentResult(),
          }));
        };
        return query?.subscribe(updateResponse, updateResponse, updateResponse);
      },
    );

    return () => {
      subscriptions.forEach((s) => s?.unsubscribe());
    };
  }, [watchQueries]);

  const errors = Object.values(responses).flatMap((r) => r?.error ?? []);
  return {
    loading: Object.values(responses).some((r) => r?.loading),
    errors: errors.length > 0 ? errors : undefined,
    data: responses,
  };
}
